<template lang="pug">
ValidationObserver(
  ref="dialogObserver"
  v-slot="{ valid }"
  slim
)
  AppDialog(
    :class="{ 'put-under': attendanceSchedulesDialogOpen, edit: editing }"
    :title="dialogTitle"
    :subtitle="subtitle"
    size="large"
    position="centered"
    show
    append-to-body
    @close="closeDialog"
  )
    form#attendance-schedule-form(
      autocomplete="off"
      onkeydown="return event.key != 'Enter';"
      @submit.prevent="onFormSubmit"
    )
      #form-container
        .row
          .col-sm-12.mb-15(:class="{ 'has-error': fieldError('user_id') }")
            AttendanceSchedulesMembersSelect(
              v-if="!editing"
              :form="form"
              @input="form.userId = $event"
            )

            .user-as-text.d-flex.align-items-center.mb-10(v-else)
              HubAvatar(
                :user-id="shift.userId"
                :users-avatars="usersAvatars"
                avatar-style="speck"
              )
              span {{ user.text }}
            span.help-block(v-show="fieldError('user_id')") {{ fieldError('user_id') | capitalize }}
        .row
          .col-sm-6
            .row
              .col-sm-12
                label.control-label(
                  required:
                  true
                ) Shift duration
              .col-sm-12.form-inline
                .row
                  .col-sm-6.attendance-schedule-datepicker(:class="{ 'has-error': fieldError('start_date') }")
                    AppDatePicker(
                      v-model="form.startDate"
                      :min-date="minDate"
                      :start-week-on="startWeekOn"
                      :show-quick-pick="false"
                      :disabled="modelType == 'Schedule' && shift && shift.isPast"
                      hide-today
                      disable-future
                      extra-picker-classes="attendance-schedule-form"
                      type="daily"
                      @input="onStartDateChange"
                    )
                    span.help-block(v-show="fieldError('start_date')") {{ fieldError('start_date') | capitalize }}
                  .col-sm-6.block-form-group.attendance-schedule-form(
                    :class="{ 'has-error': fieldError('start_time') }"
                  )
                    AppTimeInput(
                      v-model="form.startTime"
                      name="start_time"
                    )
                    span.help-block(v-show="fieldError('start_time')") {{ fieldError('start_time') | capitalize }}
          .col-sm-6
            .row
              .col-sm-12.to-label
                label.control-label To
              .col-sm-12.form-inline
                .row
                  .col-sm-6.block-form-group.to-hour-select.attendance-schedule-form(
                    :class="{ 'has-error': fieldError('stop_time') }"
                  )
                    AppTimeInput(
                      v-model="form.stopTime"
                      name="stop_time"
                    )
                    span.help-block(v-show="fieldError('stop_time')") {{ fieldError('stop_time') | capitalize }}
                  .col-sm-6.attendance-schedule-datepicker(:class="{ 'has-error': fieldError('stop_date') }")
                    AppDatePicker(
                      v-model="form.stopDate"
                      :min-date="minDate"
                      :start-week-on="startWeekOn"
                      :show-quick-pick="false"
                      :disabled="modelType == 'Schedule' && shift && shift.isPast"
                      hide-today
                      disable-future
                      extra-picker-classes="attendance-schedule-form"
                      type="daily"
                      @input="onStopDateChange"
                    )
                    span.help-block(v-show="fieldError('stop_date')") {{ fieldError('stop_date') | capitalize }}
        .row
          .col-sm-6
            AttendanceSchedulesTimeZoneSelect(
              :form="form"
              :options="formattedTimeZoneOptions"
              :default-time-zone-id="defaultTimeZoneId"
              :disabled="editDisabled"
              @updated="onSelectUpdate"
            )
          .col-sm-6(:class="{ 'has-error': fieldError('minimum_hours') }")
            AppDurationInput(
              :value="minimumHoursAsDuration"
              label="Minimum hours*"
              placeholder="hours between 0:00 and 24:00"
              with-seconds="never"
              validate-day
              data-testid="minimum-hours-input"
              @input="onMinimumHoursDurationInput"
            )
            span.help-block(v-show="fieldError('minimum_hours')") {{ fieldError('minimum_hours') | capitalize }}
        .row(v-if="modelType == 'Shift'")
          .col-sm-8(:class="{ 'has-error': fieldError('notes') }")
            label.control-label Notes
            AppInput(
              v-model="form.notes"
              type="text"
              name="notes"
            )
            span.help-block(v-show="fieldError('notes')")
              | {{ fieldError('notes') | capitalize }}
        .recurring-schedule-options(v-if="showRecurringOptions")
          .row
            .col-sm-12
              h5 Recurring shift options
          .row
            .col-sm-6
              AttendanceSchedulesRecurringSelect(
                :form="form"
                :options="repeatScheduleOptions"
                @updated="onSelectUpdate"
                @input="onRecurringChange"
              )
            .col-sm-6(v-show="recurring")
              .row
                .col-sm-12
                  AppSwitch.cursor-pointer(
                    v-model="endDatePresent"
                    label="REPEAT UNTIL"
                  )
                .col-sm-12.forever(v-if="repeatForever")
                  span Forever
                .col-sm-12.repeat-options.attendance-schedule-datepicker(
                  v-else
                  :class="{ 'has-error': fieldError('repeat_until') }"
                )
                  AppDatePicker(
                    v-model="form.repeatUntil"
                    :min-date="minDate"
                    :start-week-on="startWeekOn"
                    hide-today
                    disable-future
                    :show-quick-pick="false"
                    :extra-picker-classes="'attendance-schedule-form repeat-until'"
                    type="daily"
                  )
                  span.help-block(v-show="fieldError('repeat_until')") {{ fieldError('repeat_until') | capitalize }}
          .row.repeat-options(v-show="recurring")
            .col-sm-7(:class="{ 'has-error': fieldError('days') }")
              .row
                .col-sm-12
                  label.control-label Days
                .col-sm-12
                  AppIntervalDayPicker#weekdays(
                    v-model="form.days"
                    mode="week"
                    multiple
                  )
                  span.help-block(v-show="fieldError('days')") {{ fieldError('days') | capitalize }}

        .shift-warning.orange.mt-20(v-if="showScheduleWarning")
          .hi.hi-warning.orange.hi-12
          div {{ scheduleWarningMessage }}

    template(slot="footer")
      AppButton.pull-left.delete-btn(
        v-show="editing"
        type="danger"
        @click="$emit('deleteShift')"
      ) Delete
      AppButton(@click="closeDialog") Cancel
      button.btn.btn-primary(
        type="submit"
        form="attendance-schedule-form"
        :disabled="!valid || form.processing"
      ) Save

  RemoveAttendanceScheduleDialog(
    v-if="showRemoveScheduleDialog"
    :title="removeDialogTitle"
    :shift="shift"
    @close="closeDialog"
  )
</template>

<script>
import { mapInertiaProps } from '~/utils/inertia';
import { applyTemplate } from '~/utils/url_template';
import AttendanceSchedulesMembersSelect from '../AttendanceSchedulesMemberSelect.vue';
import AttendanceSchedulesTimeZoneSelect from './AttendanceSchedulesTimeZoneSelect.vue';
import AttendanceSchedulesRecurringSelect from './AttendanceSchedulesRecurringSelect.vue';
import RemoveAttendanceScheduleDialog from './RemoveAttendanceScheduleDialog.vue';
import AppDurationInput from '~/components/AppDurationInput.vue';
import { createUsersAvatarsMixin } from '~/mixins/UsersAvatarsMixin';

export default {
  name: 'ScheduleDialog',
  components: {
    AttendanceSchedulesMembersSelect,
    AttendanceSchedulesTimeZoneSelect,
    AttendanceSchedulesRecurringSelect,
    RemoveAttendanceScheduleDialog,
    AppDurationInput,
  },
  mixins: [createUsersAvatarsMixin('assignedMember')],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    dateRange: {
      type: Object,
      required: true,
    },
    editingAttendanceSchedule: {
      type: Object,
      default: () => ({}),
    },
    event: {
      type: Object,
      default: () => ({}),
    },
    modelType: {
      type: String,
      default: 'Schedule',
    },
  },
  data() {
    const shiftUserId = this.editingAttendanceSchedule.user_id;
    let userId = this.editingAttendanceSchedule.user_id;

    if (!userId) {
      userId = [];
    } else if (!Array.isArray(userId)) {
      userId = [userId];
    }

    return {
      attendanceSchedulesDialogOpen: false,
      showRemoveScheduleDialog: false,
      recurring: ['1-1', '1-2'].includes(this.editingAttendanceSchedule.repeat_schedule),
      repeatForever: [undefined, null].includes(this.editingAttendanceSchedule.repeat_until),
      form: this.$inertia.form({
        userId,
        id: this.editing ? this.editingAttendanceSchedule.id : null,
        minimumHours: this.editing ? this.editingAttendanceSchedule.minimum_hours : 7.52,
        startDate: moment(this.dateRange.start.format('YYYY-MM-DD'), 'YYYY-MM-DD'),
        startTime: moment(this.dateRange.start.format('hh:mm a'), 'hh:mm a'),
        stopTime: moment(this.dateRange.stop.format('hh:mm a'), 'hh:mm a'),
        stopDate: moment(this.dateRange.stop.format('YYYY-MM-DD'), 'YYYY-MM-DD'),
        effectiveTimeZone: this.editing ? this.editingAttendanceSchedule.time_zone : null,
        repeatSchedule: this.editing ? this.editingAttendanceSchedule.repeat_schedule : null,
        repeatUntil: this.editing
          ? moment(this.editingAttendanceSchedule.repeat_until || undefined)
          : moment().add(1, 'month'),
        days: this.editing ? this.editingAttendanceSchedule.days : [],
        notes: this.event?.shiftAttributes?.notes,
        errors: this.errors,
        selectAllUsers: false,
        excludedUserIds: [],
      }),
      defaultMembers: { members: shiftUserId ? [{ id: shiftUserId, selected: true }] : [] },
    };
  },
  computed: {
    ...mapInertiaProps({
      organization: 'organization',
      minDate: 'min_date',
      startWeekOn: 'start_week_on',
      repeatUntil: 'repeat_until',
      errors: 'errors',
      users: 'calendar_resources.users',
      repeatSchedule: 'repeat_schedule',
      timeZoneOptions: 'time_zones',
      members({ calendar_resources: calendarResource }) {
        return calendarResource.users
          .map(({ id, title: text, avatar, time_zone: timeZone }) => ({ id, text, avatar, timeZone }))
          .sort((ua, ub) => ua.text.localeCompare(ub.text));
      },
      activeTimeZone: 'time_zone',
    }),
    filteredUsers() {
      const filtered = this.editingAttendanceSchedule.filteredUsers || [];

      // if no user is selected, the filteredUsers array will include all users
      if (filtered.length === this.members.length) {
        return [];
      }

      return this.editing ? filtered : [];
    },
    assignedMember() {
      return this.members.filter((user) => this.shift.userId === user.id);
    },
    subtitle() {
      if (!this.editing) {
        return;
      }

      if (this.shift.isRecurring && this.modelType === 'Schedule') {
        const start = moment.utc(this.editingAttendanceSchedule.start_date).format('ddd, MMM D, YYYY');
        const end = this.editingAttendanceSchedule.repeat_until
          ? moment.utc(this.editingAttendanceSchedule.repeat_until).format('ddd, MMM D, YYYY')
          : 'Forever';

        return `${start} - ${end}`;
      }

      return moment.utc(this.event.startStr).format('ddd, MMM D, YYYY');
    },
    select2Options() {
      return {
        width: '100%',
      };
    },
    dialogTitle() {
      if (!this.editing) {
        return 'Create shift';
      }

      return 'Edit shift';
    },
    showRecurringOptions() {
      if (!this.editing) {
        return true;
      }
      if (this.modelType === 'Shift') {
        return false;
      }

      return !this.shift.isPast;
    },
    showScheduleWarning() {
      if (!this.editing) {
        return false;
      }

      return this.modelType === 'Schedule' && this.shift.isRecurring;
    },
    scheduleWarningMessage() {
      return this.shift.isPast
        ? 'This will edit this shift and all future shifts'
        : "This will edit shifts that haven't started and future shifts";
    },
    user() {
      return this.editing ? this.members.find((user) => user.id === this.form.userId[0]) : null;
    },
    userName() {
      return this.editing ? this.user.text : null;
    },
    repeatScheduleOptions() {
      return this.repeatSchedule.map((obj) => ({ id: obj[1], name: obj[0], text: obj[0] }));
    },
    formattedTimeZoneOptions() {
      return this.timeZoneOptions.map((tz) => ({
        text: tz.description === 'My timezone' ? this.memberTimeZoneOption() : `Organization (${tz.abbr})`,
        id: tz.description === 'My timezone' ? 1 : 0,
        description: tz.description,
        for: tz.for,
      }));
    },
    removeDialogTitle() {
      return this.editing && this.editingAttendanceSchedule.repeat_schedule === 0
        ? 'Remove recurring shifts'
        : 'Remove schedule';
    },
    shift() {
      return this.event.shiftAttributes;
    },
    endDatePresent: {
      get() {
        return ![undefined, null].includes(this.editingAttendanceSchedule.repeat_until);
      },
      set(value) {
        this.repeatForever = !value;
      },
    },
    defaultTimeZoneId() {
      if (this.editing) {
        return this.form.effectiveTimeZone;
      }

      return this.formattedTimeZoneOptions.find((option) => option.description === this.activeTimeZone.description).id;
    },
    editDisabled() {
      return (this.shift && this.shift.isPast) || this.modelType === 'Shift';
    },
    minimumHoursAsDuration() {
      // Converts decimal hours to seconds (that's what AppDurationInput expects)
      return moment.duration(this.form.minimumHours, 'hours').asSeconds();
    },
  },
  mounted() {
    this.$watch(
      // Make a programmatic watch property that will trigger the `updateMinimumHours` method
      // whenever any of these properties change within the vm.
      ({ form }) => [form.startTime, form.stopTime, form.startDate, form.stopDate],
      () => this.updateMinimumHours(),
      { immediate: false, deep: true },
    );
  },
  methods: {
    onStartDateChange(start) {
      if (start.isAfter(this.form.stopDate)) {
        this.form.stopDate = start.clone();
      }
    },
    memberTimeZoneOption() {
      if (this.editing) {
        const memberTimeZoneAbbr = this.members.find((user) => user.id === this.form.userId[0]).timeZone.abbr;

        return `Member (${memberTimeZoneAbbr})`;
      }

      return 'Selected member(s)';
    },
    onStopDateChange(stop) {
      if (this.form.startDate.isAfter(stop)) {
        this.form.startDate = stop.clone();
      }
    },
    updateMinimumHours() {
      const startTime = this.form.startTime;
      const stopTime = moment(this.form.stopTime);
      const startDate = this.form.startDate.clone();
      const stopDate = this.form.stopDate.clone();

      if (!startTime || !stopTime) {
        return;
      }

      startDate.set({ hours: startTime.hours(), minutes: startTime.minutes() });

      stopDate.set({ hours: stopTime.hours(), minutes: stopTime.minutes() });

      const differenceAsHours = moment.duration(stopDate.diff(startDate)).asHours() * 0.94;

      if (differenceAsHours < 0 || differenceAsHours > 24.0) {
        return;
      }
      this.form.minimumHours = differenceAsHours.toFixed(2);
    },
    fieldError(field) {
      if (this.form.errors && this.form.errors[field]) {
        return this.form.errors[field][0];
      }
    },
    onRecurringChange(val) {
      this.recurring = !(val === '0-0');
    },
    onSelectUpdate(value, field) {
      this.form[field] = value;
    },
    onMinimumHoursDurationInput(seconds) {
      // Converts seconds to decimal hours
      this.form.minimumHours = moment.duration(seconds, 'seconds').asHours();
    },
    closeDialog(id = null) {
      this.$emit('close', id);
    },
    async onFormSubmit() {
      let attendanceSchedulesUrl;

      if (this.editing) {
        attendanceSchedulesUrl = '/schedules/:scheduleId';
      } else if (
        (this.form.userId && this.form.userId.length > 1) ||
        this.form.selectAllUsers ||
        (!this.form.userId && this.filteredUsers.length > 0)
      ) {
        attendanceSchedulesUrl = '/organizations/:organizationId/schedules/bulk_create';
      } else {
        attendanceSchedulesUrl = '/organizations/:organizationId/schedules';
      }

      const attendanceShiftsUrlString = applyTemplate(`${'/organizations/:organizationId/attendance_shifts/:id'}`, {
        organizationId: this.organization.id,
        id: this.editing && this.modelType === 'Shift' ? this.shift.id : null,
      });
      const attendanceSchedulesUrlString = applyTemplate(`${attendanceSchedulesUrl}`, {
        organizationId: this.organization.id,
        scheduleId: this.editing ? this.editingAttendanceSchedule.id : null,
      });

      this.form.transform((data) => {
        const parsedData = this.modelType === 'Shift' ? this.shiftPayload(data) : this.schedulePayload(data);

        parsedData.filtered_user_ids = this.members.map((user) => user.id);

        return parsedData;
      });

      const options = { onSuccess: () => this.closeDialog() };

      if (this.editing) {
        const url = this.modelType === 'Shift' ? attendanceShiftsUrlString : attendanceSchedulesUrlString;

        return await this.form.put(url, options);
      }

      return await this.form.post(attendanceSchedulesUrlString, options);
    },
    schedulePayload(data) {
      const payload = {};

      payload.minimum_hours = data.minimumHours;
      payload.repeat_schedule = data.repeatSchedule || this.repeatScheduleOptions[0].id;
      payload.effective_time_zone = data.effectiveTimeZone ?? this.defaultTimeZoneId;
      payload.start_date = data.startDate.format('YYYY-MM-DD');
      payload.stop_date = data.stopDate.format('YYYY-MM-DD');
      payload.start_time = moment(data.startTime).format('h:mm a');
      payload.stop_time = moment(data.stopTime).format('h:mm a');
      payload.days = data.days;
      payload.select_all_users = data.selectAllUsers;
      payload.excluded_user_ids = data.excludedUserIds;

      if (this.editing) {
        payload.updatable_shift_start_time = this.shift.start_time;
      }

      if (this.repeatForever === true) {
        payload.repeat_until = null;
      } else {
        payload.repeat_until = data.repeatUntil.format('YYYY-MM-DD');
      }

      if (!this.form.userId && this.filteredUsers.length > 0) {
        payload.user_ids = this.filteredUsers.map((u) => u.id);
      } else if (data.userId === null) {
        payload.user_id = null;
      } else if (data.userId.length > 1) {
        payload.user_ids = data.userId;
      } else {
        if (Array.isArray(data.userId)) {
          payload.user_id = data.userId[0];
        } else {
          payload.user_id = data.userId;
        }
      }

      return { attendance_schedule: payload };
    },
    shiftPayload(data) {
      return {
        attendance_shift: {
          minimum_hours: data.minimumHours,
          start_date: data.startDate.format('YYYY-MM-DD'),
          stop_date: data.stopDate.format('YYYY-MM-DD'),
          start_time: moment(data.startTime).format('h:mm a'),
          stop_time: moment(data.stopTime).format('h:mm a'),
          notes: data.notes,
        },
      };
    },
  },
};
</script>
